/**
 * Therapy route components — lazy-loaded from App.tsx.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TherapySession from './TherapySession';
import TherapyInbox from './TherapyInbox';
import { patients as patientsApi } from '../../api/endpoints';

interface PatientInfo {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  age?: number;
}

/**
 * /therapy/:patientId — Session recording page.
 * Patient ID comes from route params, patient data fetched from API.
 * Shows full patient header (name, age, DOB) once loaded.
 */
export function TherapySessionRoute() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [patientInfo, setPatientInfo] = useState<PatientInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (patientId) {
      setLoading(true);
      patientsApi.get(patientId).then(res => {
        const data = res?.data as any;
        if (data) {
          const firstName = data.firstName || data.first_name || '';
          const lastName = data.lastName || data.last_name || '';
          const dob = data.dateOfBirth || data.date_of_birth || data.birthDate || '';

          // Calculate age in years;months
          let age = data.age;
          if (!age && dob) {
            try {
              const dobDate = new Date(dob);
              const now = new Date();
              age = (now.getFullYear() - dobDate.getFullYear()) * 12 +
                (now.getMonth() - dobDate.getMonth());
              if (now.getDate() < dobDate.getDate()) age -= 1;
            } catch { /* ignore */ }
          }

          setPatientInfo({
            id: patientId,
            firstName,
            lastName,
            dateOfBirth: dob,
            age: typeof age === 'number' ? age : undefined,
          });
        }
      }).catch(() => {
        setPatientInfo({ id: patientId, firstName: 'Patient', lastName: '' });
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [patientId]);

  if (!patientId) return null;

  // Build display name with age
  const displayName = patientInfo
    ? buildPatientHeader(patientInfo)
    : '';

  return (
    <TherapySession
      patientId={patientId}
      patientName={loading ? '' : displayName}
      patientInfo={patientInfo || undefined}
      onBack={() => navigate('/')}
      onComplete={() => navigate('/therapy/inbox')}
    />
  );
}

/** Format age as years;months (e.g., "2;6") from age in months */
function formatAge(ageMonths: number): string {
  const years = Math.floor(ageMonths / 12);
  const months = ageMonths % 12;
  return `${years};${months}`;
}

/** Build header string: "FirstName LastName (Age Y;M)" */
function buildPatientHeader(info: PatientInfo): string {
  const name = `${info.firstName} ${info.lastName}`.trim();
  if (info.age != null && info.age > 0) {
    return `${name} (Age ${formatAge(info.age)})`;
  }
  return name;
}

/**
 * /therapy/inbox — Draft notes batch review.
 */
export function TherapyInboxRoute() {
  return <TherapyInbox />;
}
